/*
 * wtf-size-sheet.js (R543 fix 1, 2026-10-09)
 * Bottom sheet that opens when a shopper taps "Choose a size" (the sticky bar button, or the in-form Add button
 * while no size is chosen). It shows the product title, the price, every visible option group of the REAL
 * <variant-selects> (Color, Size, anything else), a Size Chart link, and one Add to cart button.
 *
 * The real form stays the source of truth:
 *   - A tap on an option calls .click() on the matching real <input> inside <variant-selects>. The theme's own change
 *     handlers and the size-gate in wtf-ff-pdp.js run unchanged.
 *   - Craft replaces <variant-selects> after every change. The sheet re-reads the DOM after each change and updates
 *     its own buttons in place (same buttons, new state), so focus stays on the tapped button.
 *   - The sheet's Add closes the sheet, then calls window.wtfFfPdp.addToCart(), which clicks the real button[name="add"].
 *     One AddToCart event fires. Custom-field validation (House Divided teams, Tepo) shows its own message on the page.
 *
 * Mobile only (max-width 749px). Above that width open() returns false and wtf-ff-pdp.js keeps its old behaviour
 * (scroll to the size pills). Exposes window.wtfSizeSheet = { open, close, isOpen }.
 */
(function () {
  'use strict';
  if (window.wtfSizeSheet) return;

  var MQ = '(max-width: 749px)';
  var root = null;
  var panel = null;
  var titleEl = null;
  var priceEl = null;
  var groupsEl = null;
  var addBtn = null;
  var addLabel = null;

  var isOpen = false;
  var trigger = null;
  var signature = '';
  var renderTimer = 0;
  var observer = null;
  var reopenAfterDialog = false;
  var dialogObserver = null;

  function state() {
    return window.wtfFfPdp || null;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  /* WTF 2026-10-28 (onesie labels): garment groups. Only inputs that carry data-wtf-gg (printed by snippets/product-variant-options.liquid
     for the House Divided baby and toddler tee) get a group header and a garment word. With no data-wtf-gg on any input every branch
     below is skipped and the sheet builds the same DOM as before. */
  var GG = {
    onesie: {
      name: 'Onesie (bodysuit)',
      sub: 'Baby sizes',
      word: 'onesie',
      short: 'Onesie',
      icon: '<svg class="wtf-gg-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M9 3.2Q12 6.4 15 3.2L19.6 5L22.2 9.4L19.2 11.2L17.6 9V16.4Q17.6 18.6 15.4 18.6H14.2V20.6H9.8V18.6H8.6Q6.4 18.6 6.4 16.4V9L4.8 11.2L1.8 9.4L4.4 5Z" fill="#fff" stroke="#5676FF" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/><path d="M10.8 19.6H13.2" fill="none" stroke="#5676FF" stroke-width="1.8" stroke-linecap="round"/></svg>'
    },
    tee: {
      name: 'Toddler T-shirt',
      sub: '2T to 5T',
      word: 'toddler T-shirt',
      short: 'Tee',
      icon: '<svg class="wtf-gg-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M8.6 3.4Q12 6 15.4 3.4L20.6 5.6L22.4 9.6L18.8 10.8V20H5.2V10.8L1.6 9.6L3.4 5.6Z" fill="#fff" stroke="#5676FF" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/></svg>'
    }
  };

  function ggWord(kind) {
    return GG[kind] ? GG[kind].word : '';
  }

  function buildGroupHead(kind, isFirst) {
    var info = GG[kind];
    var head = make('div', 'wtf-gg-head' + (isFirst ? ' wtf-gg-head--first' : ''));
    head.setAttribute('data-wtf-gg', kind);
    head.setAttribute('aria-hidden', 'true');
    head.innerHTML = info.icon;
    head.appendChild(make('span', 'wtf-gg-name', info.name));
    head.appendChild(make('span', 'wtf-gg-sub', info.sub));
    return head;
  }

  function isMobile() {
    return !!(window.matchMedia && window.matchMedia(MQ).matches);
  }

  function isSizeName(name) {
    return String(name || '').indexOf('Size') === 0;
  }

  function hintText() {
    var st = state();
    var text = st && st.getHintText ? st.getHintText() : '';
    return text || 'Choose a size';
  }

  function productTitle() {
    var top = document.querySelector('.wtf-pdp-top__title');
    if (top && top.textContent.trim()) return top.textContent.trim();
    var vs = document.querySelector('variant-selects[data-product-title]');
    if (vs && vs.getAttribute('data-product-title')) return vs.getAttribute('data-product-title');
    var h1 = document.querySelector('.product__title h1, .product__title h2');
    return h1 ? h1.textContent.trim() : '';
  }

  /* R543 round 2: after an option change Dawn inserts the NEW <variant-selects> before the old one, sets style="display:none"
     on the old one, and removes it about 500 ms later (HTMLUpdateUtility.viewTransition). For that half second two copies
     are in the DOM. The sheet must read only the live one: the first copy that is not display:none and has a box.
     Fallbacks keep the old behaviour when nothing qualifies. */
  function liveSelects() {
    var all = toArray(document.querySelectorAll('variant-selects'));
    if (!all.length) return null;
    var visible = all.filter(function (vs) {
      return vs.style.display !== 'none' && !vs.closest('[hidden]') && vs.getClientRects().length > 0;
    });
    if (visible.length) return visible[0];
    var notNone = all.filter(function (vs) {
      return vs.style.display !== 'none';
    });
    return notNone.length ? notNone[0] : all[0];
  }

  /* Read every visible option group out of the live <variant-selects>. */
  function readGroups() {
    var groups = [];
    var live = liveSelects();
    if (!live) return groups;
    toArray(live.querySelectorAll('fieldset')).forEach(function (fs) {
      if (fs.hidden || fs.hasAttribute('data-wtf-hidden-option') || fs.style.display === 'none') return;
      var radios = toArray(fs.querySelectorAll('input[type="radio"]'));
      if (!radios.length) return;
      var name = radios[0].getAttribute('data-option-name');
      if (!name) {
        var legend = fs.querySelector('legend');
        var first = legend && legend.firstChild ? legend.firstChild.textContent : '';
        name = String(first || '').replace(/:\s*$/, '').trim();
      }
      var opts = radios.map(function (input) {
        var label = input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL' ? input.nextElementSibling : null;
        if (!label && input.id) label = fs.querySelector('label[for="' + input.id.replace(/"/g, '\\"') + '"]');
        return {
          input: input,
          value: input.value,
          checked: input.checked,
          reallyDisabled: input.disabled,
          soldOut: input.classList.contains('disabled') || input.classList.contains('visually-disabled') || input.disabled,
          swatch: label ? label.querySelector('.swatch') : null,
          gg: input.getAttribute('data-wtf-gg') || ''
        };
      });
      groups.push({ name: name, isSize: isSizeName(name), opts: opts });
    });
    return groups;
  }

  function getSizeChartButton() {
    return (
      document.querySelector('.wtf-ff-sizechart-opener--clone [data-wtf-ff-sizechart-open]') ||
      document.querySelector('[data-wtf-ff-sizechart-source] [data-wtf-ff-sizechart-open]') ||
      document.querySelector('[data-wtf-ff-sizechart-open]')
    );
  }

  function build() {
    if (root) return;
    root = make('div', 'wtf-sheet');
    root.id = 'wtf-size-sheet';
    root.hidden = true;

    var backdrop = make('div', 'wtf-sheet__backdrop');
    backdrop.addEventListener('click', function () {
      close();
    });

    panel = make('div', 'wtf-sheet__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'wtf-sheet-title');
    panel.setAttribute('tabindex', '-1');

    var closeBtn = make('button', 'wtf-sheet__close');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
    closeBtn.addEventListener('click', function () {
      close();
    });

    var head = make('div', 'wtf-sheet__head');
    titleEl = make('p', 'wtf-sheet__title');
    titleEl.id = 'wtf-sheet-title';
    priceEl = make('p', 'wtf-sheet__price');
    head.appendChild(titleEl);
    head.appendChild(priceEl);

    groupsEl = make('div', 'wtf-sheet__groups');

    addBtn = make('button', 'wtf-sheet__add');
    addBtn.type = 'button';
    addLabel = make('span', '', 'Add to cart');
    addBtn.appendChild(addLabel);
    addBtn.addEventListener('click', onAdd);

    panel.appendChild(closeBtn);
    panel.appendChild(head);
    panel.appendChild(groupsEl);
    panel.appendChild(addBtn);
    root.appendChild(backdrop);
    root.appendChild(panel);
    document.body.appendChild(root);

    root.addEventListener('keydown', onKeydown);
  }

  function buildOption(group, opt) {
    var b = make('button', 'wtf-sheet__opt');
    b.type = 'button';
    b.setAttribute('data-opt-name', group.name);
    b.setAttribute('data-opt-value', opt.value);
    if (opt.swatch) {
      var dot = opt.swatch.cloneNode(true);
      dot.classList.add('wtf-sheet__swatch');
      dot.removeAttribute('title');
      dot.setAttribute('aria-hidden', 'true');
      b.appendChild(dot);
    }
    b.appendChild(make('span', 'wtf-sheet__opt-text', opt.value));
    if (opt.gg && GG[opt.gg]) {
      b.classList.add('wtf-sheet__opt--gg');
      b.appendChild(make('span', 'visually-hidden', ', ' + ggWord(opt.gg)));
      var wordEl = make('span', 'wtf-gg-word', GG[opt.gg].short);
      wordEl.setAttribute('aria-hidden', 'true');
      b.appendChild(wordEl);
    }
    b.addEventListener('click', onOptionTap);
    return b;
  }

  function rebuildGroups(groups) {
    while (groupsEl.firstChild) groupsEl.removeChild(groupsEl.firstChild);
    var hasChart = !!getSizeChartButton();
    groups.forEach(function (group, gi) {
      var wrap = make('div', 'wtf-sheet__group');
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('data-group-name', group.name);
      var legend = make('div', 'wtf-sheet__legend');
      legend.id = 'wtf-sheet-g' + gi;
      wrap.setAttribute('aria-labelledby', legend.id);
      var labelWrap = make('span', 'wtf-sheet__legend-label');
      labelWrap.appendChild(make('span', 'wtf-sheet__legend-name', group.name + (group.isSize ? '' : ':')));
      labelWrap.appendChild(make('span', 'wtf-sheet__legend-val', ''));
      legend.appendChild(labelWrap);
      if (group.isSize && hasChart) {
        var chart = make('button', 'wtf-sheet__chart', 'Size Chart');
        chart.type = 'button';
        chart.addEventListener('click', onSizeChart);
        legend.appendChild(chart);
      }
      var row = make('div', 'wtf-sheet__opts' + (group.isSize ? '' : ' wtf-sheet__opts--scroll'));
      var prevGg = '';
      group.opts.forEach(function (opt) {
        if (opt.gg && GG[opt.gg] && opt.gg !== prevGg) {
          row.appendChild(buildGroupHead(opt.gg, !prevGg));
          prevGg = opt.gg;
        }
        row.appendChild(buildOption(group, opt));
      });
      wrap.appendChild(legend);
      wrap.appendChild(row);
      groupsEl.appendChild(wrap);
    });
  }

  function updateState(groups) {
    var st = state();
    var chosen = st ? st.isSizeChosen() : true;
    var wraps = toArray(groupsEl.querySelectorAll('.wtf-sheet__group'));
    groups.forEach(function (group, gi) {
      var wrap = wraps[gi];
      if (!wrap) return;
      var buttons = toArray(wrap.querySelectorAll('.wtf-sheet__opt'));
      var selectedValue = '';
      group.opts.forEach(function (opt, oi) {
        var b = buttons[oi];
        if (!b) return;
        var on = opt.checked && !(group.isSize && !chosen);
        if (on) selectedValue = opt.value;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('is-soldout', !!opt.soldOut);
        if (opt.reallyDisabled) b.setAttribute('aria-disabled', 'true');
        else b.removeAttribute('aria-disabled');
        b.setAttribute('aria-label', opt.value + (opt.gg && GG[opt.gg] ? ', ' + ggWord(opt.gg) : '') + (opt.soldOut ? ', sold out' : ''));
      });
      var val = wrap.querySelector('.wtf-sheet__legend-val');
      if (val) {
        var text = group.isSize ? '' : selectedValue;
        if (val.textContent !== text) val.textContent = text;
      }
      var row = wrap.querySelector('.wtf-sheet__opts');
      if (row) {
        row.classList.toggle('is-overflowing', row.scrollWidth > row.clientWidth + 2);
      }
      wrap.classList.toggle('wtf-sheet__group--need', group.isSize && !chosen && wrap.getAttribute('data-need') === '1');
    });
  }

  function updateHeader() {
    var st = state();
    var title = productTitle();
    if (titleEl.textContent !== title) titleEl.textContent = title;
    while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);
    var price = st && st.getPriceText ? st.getPriceText() : '';
    var compare = st && st.getCompareText ? st.getCompareText() : '';
    if (price) {
      if (compare) priceEl.appendChild(make('s', 'wtf-sheet__compare', compare));
      priceEl.appendChild(make('span', 'wtf-sheet__now', price));
    }
  }

  function updateAdd() {
    var st = state();
    var chosen = st ? st.isSizeChosen() : true;
    var rb = st ? st.getRealButton() : null;
    var label;
    if (!chosen) {
      label = hintText();
    } else {
      var span = rb ? rb.querySelector('span') : null;
      label = span && span.textContent.trim() ? span.textContent.trim() : 'Add to cart';
      if (label === hintText()) label = 'Add to cart';
    }
    if (addLabel.textContent !== label) addLabel.textContent = label;
    var disabled = chosen && (!rb || !!rb.disabled);
    addBtn.disabled = disabled;
    addBtn.classList.toggle('is-need-size', !chosen);
  }

  function render(force) {
    if (!root) return;
    var groups = readGroups();
    updateHeader();
    updateAdd();
    if (!groups.length) return;
    var sig = groups
      .map(function (g) {
        return g.name + ':' + g.opts.map(function (o) { return o.value; }).join('/');
      })
      .join('|');
    if (force || sig !== signature || !groupsEl.firstChild) {
      signature = sig;
      rebuildGroups(groups);
    }
    updateState(groups);
  }

  function scheduleRender() {
    if (!isOpen) return;
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(function () {
      render(false);
    }, 50);
  }

  function centerSelected() {
    toArray(groupsEl.querySelectorAll('.wtf-sheet__opts--scroll')).forEach(function (row) {
      var sel = row.querySelector('[aria-pressed="true"]');
      if (!sel) return;
      row.scrollLeft = Math.max(0, sel.offsetLeft - (row.clientWidth - sel.offsetWidth) / 2);
    });
  }

  /* Find the live real input for a sheet button: same group name and same value, read fresh at tap time. */
  function findRealInput(name, value) {
    var live = liveSelects();
    var inputs = live ? toArray(live.querySelectorAll('input[type="radio"]')) : [];
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].getAttribute('data-option-name') === name && inputs[i].value === value) return inputs[i];
    }
    return null;
  }

  function onOptionTap(event) {
    var b = event.currentTarget;
    var input = findRealInput(b.getAttribute('data-opt-name'), b.getAttribute('data-opt-value'));
    if (!input || input.disabled) return;
    input.click();
    scheduleRender();
  }

  function onAdd() {
    var st = state();
    if (!st) return;
    if (!st.isSizeChosen()) {
      var need = groupsEl.querySelector('.wtf-sheet__group[data-group-name^="Size"]');
      if (need) {
        need.setAttribute('data-need', '1');
        need.classList.add('wtf-sheet__group--need');
        var firstOpt = need.querySelector('.wtf-sheet__opt');
        if (firstOpt) {
          try { firstOpt.focus({ preventScroll: false }); } catch (e) { firstOpt.focus(); }
        }
      }
      return;
    }
    var rb = st.getRealButton();
    if (!rb || rb.disabled) return;
    close();
    st.addToCart();
  }

  function onSizeChart() {
    var btn = getSizeChartButton();
    if (!btn) return;
    /* The size dialog sits below the sheet (z-index 101 vs 500): close the sheet, open the dialog, reopen the sheet when it closes. */
    reopenAfterDialog = true;
    watchDialogClose();
    close({ restoreFocus: false });
    btn.click();
  }

  function watchDialogClose() {
    var dlg = document.querySelector('.wtf-ff-size-dialog');
    if (dialogObserver) {
      dialogObserver.disconnect();
      dialogObserver = null;
    }
    if (!dlg || !('MutationObserver' in window)) {
      reopenAfterDialog = false;
      return;
    }
    var seenOpen = false;
    dialogObserver = new MutationObserver(function () {
      if (dlg.hasAttribute('open')) {
        seenOpen = true;
        return;
      }
      if (!seenOpen) return;
      dialogObserver.disconnect();
      dialogObserver = null;
      if (reopenAfterDialog) {
        reopenAfterDialog = false;
        window.setTimeout(function () {
          if (!isOpen) open(trigger);
        }, 60);
      }
    });
    dialogObserver.observe(dlg, { attributes: true, attributeFilter: ['open'] });
    /* Safety: if the dialog never opens, do not leave a stale reopen flag. */
    window.setTimeout(function () {
      if (!seenOpen && dialogObserver) {
        dialogObserver.disconnect();
        dialogObserver = null;
        reopenAfterDialog = false;
      }
    }, 1500);
  }

  function focusables() {
    return toArray(
      panel.querySelectorAll('button:not([disabled]):not([aria-disabled="true"]), [href], [tabindex]:not([tabindex="-1"])')
    ).filter(function (n) {
      return n.offsetWidth > 0 || n.offsetHeight > 0;
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var list = focusables();
    if (!list.length) {
      event.preventDefault();
      return;
    }
    var first = list[0];
    var last = list[list.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Craft calls .focus() on the real input after it re-renders. Pull focus back to the matching sheet button. */
  function onFocusIn(event) {
    if (!isOpen || !root || root.contains(event.target)) return;
    var t = event.target;
    var target = null;
    if (t && t.tagName === 'INPUT' && t.type === 'radio') {
      var buttons = toArray(groupsEl.querySelectorAll('.wtf-sheet__opt'));
      for (var i = 0; i < buttons.length; i++) {
        if (
          buttons[i].getAttribute('data-opt-name') === t.getAttribute('data-option-name') &&
          buttons[i].getAttribute('data-opt-value') === t.value
        ) {
          target = buttons[i];
          break;
        }
      }
    }
    var dest = target || panel;
    try { dest.focus({ preventScroll: true }); } catch (e) { dest.focus(); }
  }

  function startObserving() {
    var container = document.querySelector('.product__info-container') || document.querySelector('.product__info-wrapper');
    if (container && 'MutationObserver' in window) {
      observer = new MutationObserver(scheduleRender);
      observer.observe(container, { childList: true, subtree: true });
    }
    document.addEventListener('change', scheduleRender, true);
    document.addEventListener('focusin', onFocusIn, true);
  }

  function stopObserving() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.removeEventListener('change', scheduleRender, true);
    document.removeEventListener('focusin', onFocusIn, true);
    window.clearTimeout(renderTimer);
  }

  function open(from) {
    if (!isMobile()) return false;
    var st = state();
    if (!st) return false;
    build();
    var groups = readGroups();
    if (!groups.length) return false;
    if (isOpen) return true;
    trigger = from || document.activeElement || null;
    groupsEl.querySelectorAll('.wtf-sheet__group').forEach(function (g) {
      g.removeAttribute('data-need');
      g.classList.remove('wtf-sheet__group--need');
    });
    render(true);
    root.hidden = false;
    document.documentElement.classList.add('wtf-sheet-open');
    document.body.classList.add('wtf-sheet-open');
    isOpen = true;
    startObserving();
    render(false); /* measure the colour row now that the sheet has a size (is-overflowing fade) */
    centerSelected();
    try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    return true;
  }

  function close(opts) {
    if (!isOpen) return;
    isOpen = false;
    stopObserving();
    root.hidden = true;
    document.documentElement.classList.remove('wtf-sheet-open');
    document.body.classList.remove('wtf-sheet-open');
    if (!opts || opts.restoreFocus !== false) {
      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
        try { trigger.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }
  }

  window.wtfSizeSheet = {
    open: open,
    close: close,
    isOpen: function () {
      return isOpen;
    }
  };
})();
